import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import styled, { css } from 'styled-components';
import { $ as $f7dceffc5ad7768b$export$4e328f61c538687f } from '../../3rdPartyModules/_react-aria/focus/dist/useFocusRing-BO5gj-B1.js';
import { $ as $3ef42575df84b30b$export$9d1611c77c2fe928 } from '../../3rdPartyModules/_react-aria/utils/dist/mergeProps-Bjtexw5f.js';
import { i as isMobile } from '../../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { u as useTablistContext } from '../TablistContext-DrXcHBdK.js';
import { L as Link } from '../../Text/Link/Link-CyusQ8lJ.js';
import { T as TextButton } from '../../Button/TextButton/TextButton-CgSjDrqi.js';
import { K as KEYBOARD_KEYNAMES } from '../../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';

const tabMarginsBase = ({
  navigation
}) => `
  margin-inline-end: ${navigation.tabs.marginEndBase};
  :last-of-type {
    margin-inline-end: 0;
  }
`;
const tabMarginsSmall = ({
  navigation
}) => `
  margin-inline-end: ${navigation.tabs.marginEndSmall};
  :last-of-type {
    margin-inline-end: 0;
  }
`;
const tabStylesBase = ({
  navigation
}) => `
  font-size: ${navigation.tabs.base};
  line-height: 22px;
  padding: ${navigation.tabs.paddingAllBase};
`;
const focusedTabStyle = /*#__PURE__*/css([":focus{outline:2px solid ", ";outline-offset:-2px;box-shadow:none;}"], ({
  theme
}) => theme.palette.auiblue);
const activeTabStyle = ({
  navigation
}) => `
  border-bottom: ${navigation.tabs.activeBottomBorder};
  font-weight: ${navigation.tabs.activeFontWeight};
  color: ${navigation.tabs.activeColor};
  outline: none;
`;
const defaultTabStyle = ({
  navigation
}) => `
  border-bottom: ${navigation.tabs.bottomBorder};
  font-weight: ${navigation.tabs.fontWeight};
`;
const tabStyle = (label, {
  navigation
}) => `
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  margin: 0;
  cursor: pointer;
  height: auto;
  color: ${navigation.tabs.color};

  flex-direction: column;
  justify-content: space-between;

  :hover {
    border-bottom: ${navigation.tabs.activeBottomBorder};
    color: ${navigation.tabs.color};
    text-decoration: none;
  }

  :active, :visited {
    color: ${navigation.tabs.color};
  }

  :link {
    color: ${navigation.tabs.color};
    text-decoration: none;
  }

  ::after {
    content: "${label}";
    content: "${label}" / "";
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-weight: bold;

    @media speech {
      display: none;
    }
  }
`;
const tabStylesSmall = ({
  navigation
}) => `
  font-size: ${navigation.tabs.small};
  line-height: 1;
  padding: ${navigation.tabs.paddingAllSmall};
`;
const ButtonTab = /*#__PURE__*/styled(TextButton).attrs(({
  onFocus,
  onBlur
}) => {
  const {
    isFocusVisible,
    focusProps
  } = $f7dceffc5ad7768b$export$4e328f61c538687f();
  return {
    /*
     *`useFocusRing()` uses `onFocus` and `onBlur` props, so `mergeProps()` must be used to
     * make sure user supplied `onFocus` and `onBlur` are also called.
    */
    ...$3ef42575df84b30b$export$9d1611c77c2fe928({
      onFocus,
      onBlur
    }, focusProps),
    focusVisible: isFocusVisible
  };
}).withConfig({
  componentId: "sc-storm-ui-30054544__sc-1kglx9i-0"
})(["&&{", ";", ";", " ", "}"], ({
  $label,
  theme
}) => tabStyle($label, theme), ({
  theme,
  $active
}) => $active ? activeTabStyle(theme) : defaultTabStyle(theme), ({
  theme,
  size
}) => {
  switch (size) {
    case 'small':
      return tabStylesSmall(theme);
    default:
      return tabStylesBase(theme);
  }
}, ({
  focusVisible
}) => focusVisible && isMobile(focusedTabStyle));
const TextTab = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-storm-ui-30054544__sc-1kglx9i-1"
})(["&&{", ";", ";color:", ";", "}"], ({
  theme,
  size
}) => {
  switch (size) {
    case 'small':
      return tabMarginsSmall(theme);
    default:
      return tabMarginsBase(theme);
  }
}, ({
  theme,
  size
}) => {
  switch (size) {
    case 'small':
      return tabStylesSmall(theme);
    default:
      return tabStylesBase(theme);
  }
}, ({
  theme
}) => theme.navigation.tabs.color, isMobile(css(["white-space:nowrap;"])));
const handleKeyDown = event => {
  // By default, buttons handle the Enter key on KeyDown. We prevent this because we want
  // to listen to KeyUp to prevent repeat triggers.
  if (event.key === KEYBOARD_KEYNAMES.ENTER) {
    event.preventDefault();
  }
};
const handleKeyUp = event => {
  const {
    key
  } = event;
  //  Dispatch a fake mouse click to the Tab when a ENTER or SPACE key are released
  if (key === KEYBOARD_KEYNAMES.ENTER || key === KEYBOARD_KEYNAMES.SPACE) {
    const {
      currentTarget
    } = event;
    const mouseEvent = new MouseEvent('click', {
      view: currentTarget.ownerDocument.defaultView,
      bubbles: true,
      cancelable: true
    });
    currentTarget.dispatchEvent(mouseEvent);
    event.preventDefault();
  }
};
const Tab = ({
  active,
  dataTaktAttributes,
  href,
  label,
  onClick,
  size,
  ...rest
}) => {
  const {
    isTablist
  } = useTablistContext();
  if (href) {
    return /*#__PURE__*/React__default.createElement("span", _extends({}, isTablist ? {
      role: 'tab'
    } : {}, {
      "aria-selected": active
    }), /*#__PURE__*/React__default.createElement(ButtonTab, _extends({}, dataTaktAttributes, rest, {
      as: Link,
      $label: typeof label === 'string' ? label : '',
      size: size,
      $active: active,
      onClick: onClick,
      href: href,
      role: "link"
    }, isTablist ? {
      tabIndex: active ? 0 : -1
    } : {}), label));
  }
  if (onClick) {
    return /*#__PURE__*/React__default.createElement(ButtonTab, _extends({}, dataTaktAttributes, rest, {
      $label: typeof label === 'string' ? label : '',
      size: size,
      $active: active,
      "aria-selected": active,
      onClick: onClick
    }, isTablist ? {
      tabIndex: active ? 0 : -1,
      role: 'tab',
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp
    } : {}), label);
  }
  return /*#__PURE__*/React__default.createElement(TextTab, _extends({}, rest, {
    size: size
  }, isTablist ? {
    tabIndex: -1,
    role: 'tab'
  } : {}), label);
};

export { Tab as T, tabMarginsSmall as a, tabMarginsBase as t };
//# sourceMappingURL=Tab-CLZViMm3.js.map
